<script setup lang="ts">
import type { CSSProperties } from 'vue'
import type { OnActiveItem } from './ProductSelect.vue'
import type { BreadcrumItem, HiddenBreadcrum } from './BreadcrumsLine.vue'

const t2 = useT2({
  Meter: 'Metras',
  Ratings: 'Įvertinimai',
  'Answered questions': 'Atsakyti klausimai',
  'View all variants': 'Peržiūrėti visus variantus',
  'Excl. VAT': 'Be PVM',
  'Exclusive pricing for Trade Partners': 'Išskirtinės kainos verslo partneriams',
  review: 'atsiliepimas',
  reviews: 'atsiliepimai',
  'Be the first to review': 'Palikite pirmą atsiliepimą',
})

export type ProductViewOptions = {
  linkAttr?: string
  departmentAttr?: string
  fixedSeller?: boolean
}

export type Props = {
  options?: ProductViewOptions
  productSlug?: string | None
  loading: boolean
  product: ProductListingFragmentFull | None
  mobile: boolean
  hidesimilar?: boolean
  onOptionChange?: OnActiveItem
}

const props = defineProps<Props>()
const client = useApollo('get')
const elem = useTemplateRef('elem')
const elemRight = useTemplateRef('elemRight')
const init1 = useInited('init1')
const addToHistory = useDelayInit(3333)
const auth = useAuth()

function toBox(): gen.ProductListingBoxFragment | undefined {
  const slug = props.productSlug
  if (!props.product && slug) {
    const all = helpers.toObject(client.cache.extract(false))
    for (const key in all) {
      if (key.startsWith('ProductListingBox')) {
        const asd = all[key] as gen.ProductListingBoxFragment
        if (asd?.slug === slug) return asd
      }
    }
  }
}

const data = shallowReactive({
  productBox: props.product ? undefined : toBox(),
  variants: false,
  rightStyle: {} as CSSProperties,
  mainReplace: undefined as gen.ImageNodeItemFragment | undefined,
})

function onImage(img: gen.ImageNodeItemFragment | None): void {
  data.mainReplace = img || undefined
}

const ui = computed(() => {
  const prod = props.product
  const box = data.productBox
  const min = helpers.toPositive(prod?.settingsQty?.min, 1)
  const stock = prod?._values?.stock
  const full = helpers.arr(prod?.media?.images)
  const media: gen.ImageTaggedFragment[] = full.length ? full : helpers.arrify(box?.images).map((image2) => ({ img: { image2 } }))

  const uiPrice = prod?._values?._uiPrice || box?._values?._uiPrice
  return {
    media,
    uiPrice,
    title: prod?.title || box?.title,
    inStock: (helpers.isDef(stock) && stock >= min) || !!box?._values?.inStock,
    isNew: !!prod?.isNew || !!box?.isNew,
    desk: prod?.descriptionShort,
    qtyLabel: prod?.settingsQty?.unit === 'Meter' ? t2('Meter') : undefined,
    price: uiPrice?.price?.priceForHumans,
    priceTaxless: uiPrice?.priceTaxless?.priceForHumans,
    oldPrice: uiPrice?.priceOld?.priceForHumans,
  }
})

const b2bView = computed((): boolean => {
  if (!init1.value) return false
  const p = props.product
  const on = p?.privateVisibility && auth.value.privatePricings?.find((i) => i.sellerId === p?.sellerId)
  return !!on && (p?._values?.variantAvailableCount || 0) > 1
})

const cssPad = computed(() => (props.mobile ? '_pl1 _pr1' : '_pl3 _pr2'))

const { myLink } = useLinks()

useHead({
  title: () => props.product?.title || t2('Product'),
  meta: () => {
    if (props.loading || props.product) return []
    return [{ name: 'robots', content: 'noindex, follow' }]
  },
})

const hiddenBreadcrumbs = computed((): HiddenBreadcrum[] => {
  return [
    { name: t2('Home'), to: myLink('index') },
    { name: t2('Products'), to: myLink('search') },
  ]
})

const breadcrumbList = computed((): BreadcrumItem[] => {
  const linkAttr = props.options?.departmentAttr || 'search-all'
  return helpers
    .arr(props.product?.depertments2, (item, index): BreadcrumItem[] | undefined => {
      if (!item?.length) return
      const lastIndex = item?.length - 1
      return helpers.arr(item, (item2, index2): BreadcrumItem | undefined => {
        const title = item2?.title
        const slug = item2.key
        if (!slug || !title) return
        return {
          key: `${index}:${index2}`,
          to: myLink(linkAttr, { all: ['d', slug] }),
          title,
          department: slug,
          start: !index2,
          lastnoline: index2 === lastIndex,
        }
      })
    })
    .flat()
})

if (import.meta.server) {
  useSeoProduct(() => ({ product: props.product, breadcrumbs: { list: breadcrumbList.value, hidden: hiddenBreadcrumbs.value } }))
}
</script>

<template>
  <div>
    <LazyProductViewNotFound v-if="!loading && !product" :product-slug />
    <section v-else :class="!mobile && '_row'" class="_relative">
      <div class="_relative _wmin0 _flex">
        <ProductMobileImage v-if="mobile" :media="ui.media" :title="ui.title" />
        <ProductImageCollage2 v-else :product :media="ui.media" :main-replace="data.mainReplace" />
        <div v-if="!hidesimilar && !mobile && product" style="min-height: 1400px">
          <LazyProductViewLeft v-if="init1" :product :mobile="false" :options />
        </div>
      </div>

      <div class="_relative _col _stretch" :class="[!mobile && '_noflex _hm100vh', $clsClass('product-info')]" :style="!mobile && 'width: 630px;'">
        <ClientOnly>
          <LazyProductViewUiNew v-if="ui.isNew" :mobile />
        </ClientOnly>

        <div ref="elemRight" :class="[mobile ? '_pt1' : $style.d2]" :style="data.rightStyle">
          <div :class="[cssPad, !mobile && '_mt1']" class="_row _gap04 d_hm44 m_hm80 _ctrY">
            <BreadcrumsLine v-if="product?.depertments2?.length" :list="breadcrumbList" :hidden="hiddenBreadcrumbs" :pagename="product.title" />
            <UiLoadingBox v-else-if="!product && loading" height="_h14" :repeat="4" width="_w120" />
          </div>

          <ProductUiReviews :class="cssPad" class="_mt1 _mb1" :rating="product?.rating"><PostUiStars :count="1" :value="1" class="_c45 _svg18" />{{ t2('Be the first to review') }}</ProductUiReviews>

          <div :class="cssPad" class="_relative _c1">
            <!-- <ProfileBox
              v-if="product?.seller?._uiProfile"
              seller
              product-seller
              :class="$style.sellerProfile"
              :profile="product?.seller?._uiProfile"
            /> -->

            <h1 class="_small _pr4 _hm50 _relative _fs15">{{ ui.title }}</h1>

            <!-- <div :class="$style.collectionName">
                <LinkPreview
                  v-if="product?.collection?.doc?.title?.value"
                  type="SellerArticle"
                  :get="product.collection.doc.slug"
                  :txt="ts(product?.collection?.doc?.title?.value)"
                  :to="links.article(product.collection.doc.slug, sellerHandle)"
                />
              </div> -->
            <!-- </div> -->

            <div class="_row _ctrY _gap1 _hm44 m_pt14 _wrap">
              <template v-if="!product?.isDiscontinued">
                <span v-if="ui.price" class="_br99 _pt02 _pb02 _pl1 _pr1 _mt04 _c0 _lh1" :class="[ui.oldPrice ? '_bgRed' : '_bg1']">
                  <b class="_mono _ls4 _fs14">{{ ui.price }}</b
                  ><template v-if="ui.qtyLabel"
                    ><b class="_fs09 _pl08">{{ ui.qtyLabel }}</b></template
                  >
                </span>
                <LinkFalling v-else :txt="t2('Exclusive pricing for Trade Partners')" :to="{ name: 'b2b' }" :class="$style.price" class="_label" />

                <s v-if="ui.oldPrice" class="_mono _fs09">{{ ui.oldPrice }}</s>
                <span v-if="ui.priceTaxless" class="_pl1 _fs08 _fw-l"
                  >{{ ts(t2('Excl. VAT')) }}<span class="_mono _ml04 _fw-xxl _ls-xxxs _fs08">{{ ui.priceTaxless }}</span></span
                >
              </template>

              <template v-for="(uiPrice, index) of ui.uiPrice?.lineItems" :key="index">
                <template v-for="discount in uiPrice.ui" :key="discount.amount">
                  <UiMsg v-if="discount?.msg" :txt="discount.amount" :sub="discount.msg" />
                </template>
              </template>

              <ClientOnly v-if="init1 && ui.uiPrice?.endAt">
                <LazyProductEndsIn :end-at="ui.uiPrice?.endAt" />
              </ClientOnly>
            </div>

            <div v-if="ui.uiPrice?._limited && !product?.isDiscontinued" class="_h80">
              <UiGrow :open="init1">
                <LazyProductViewPriceLimit :ui-price="ui.uiPrice" />
              </UiGrow>
            </div>

            <LazyProductViewPublicCode v-if="product?.sellerId && (ui.uiPrice?.publicCode?.code || init1)" :price="ui.uiPrice" :seller-id="product?.sellerId" />

            <div v-if="ui.desk || (!product && loading)" class="_short _fs1 _pb2 d_pt2 m_pt4 _ws_normal m_pr04 d_pr4 _hm140 _relative">
              {{ ui.desk }}
              <template v-if="!product && loading">
                <UiLoadingBox :repeat="3" height="_h14" width="_w70ch" mb="_mb04" />
                <UiLoadingBox height="_h14" width="_w220" mb="_mb04" />
              </template>
            </div>
          </div>

          <div v-if="b2bView && product" class="d_pl2 _pb4 _hm140">
            <LazyProductInfoVariants :product xs />
          </div>

          <template v-if="!product?.privateVisibility">
            <ProductSelect :product :on-option-change :class="cssPad" :on-image class="_pt2 _pb1" />

            <div v-if="!product?.privateVisibility && product?._values?.variantCount && product?._values?.variantCount > 1" class="_pb2 d_pl3 _hm64">
              <template v-if="init1">
                <ButtonInline block :open="data.variants" class="_pl04" :txt="t2('View all variants')" :sup="product?._values.variantCount" @click.passive="data.variants = !data.variants" />

                <UiGrow :open="data.variants">
                  <LazyProductInfoVariants :product />
                </UiGrow>
              </template>
            </div>
          </template>

          <div v-if="product?.quantityLimit && !product?.isDiscontinued" :class="cssPad" class="_pt2 _pb2">
            <LazyProductLimit v-if="init1" :product />
          </div>

          <!-- <div v-if="product?.availabilityType === 'Upcoming'" ref="elem" :class="cssPad" class="_pt4 _pb2">
            <LazyProductUpcoming :product />
          </div> -->

          <div v-else ref="elem" :class="[cssPad, $style.buy]" class="_col _pt2 _gap1 _hm140">
            <div class="_hm60">
              <template v-if="product">
                <ProductBuy :product save="after" :streach="mobile" :stock="product?._values?.stock">
                  <div v-if="!product?.isDiscontinued">
                    <LazyBuyerNotify :product-id="product?.id" :in-stock="!!product?._values?.inStock" :product-price="helpers.toPositive(ui.uiPrice?.price.number, 0)" />
                  </div>
                </ProductBuy>
              </template>
            </div>

            <LazyAppSalesStopped v-if="init1 && product" :key="product.sellerId || props.product?.id" :seller="undefined" :seller-id="product.sellerId" />
          </div>

          <LazyIdleProductViewExtras v-if="product" :product :mobile :class-pad="cssPad" :options hydrate-on-idle />
        </div>
      </div>
    </section>

    <LazyProductViewAddToHistory v-if="addToHistory && product && !product?.isDiscontinued" :product />
    <div v-if="product" :style="`min-height:${mobile ? '1700px' : '520px'}`">
      <LazyProductViewLazy v-if="init1" v-model:right-style="data.rightStyle" :product :elem :elem-right :in-stock="ui.inStock" :hidesimilar :options />
    </div>
  </div>
</template>

<style module>
.d2 {
  composes: global(_fs1);
  composes: global(_pt-nav);
  will-change: position top;
}
</style>
